import axiosInstance from '@/app/api/axiosInstance';
import { 
  SellableUnit, 
  SellableUnitCreate, 
  SellableUnitUpdate, 
  ItemWithSellableUnits,
  SellableUnitsSyncResponse 
} from '@/app/types/sellableUnit';

// Helper to parse numeric fields
const parseSellableUnitResponse = (unit: SellableUnit): SellableUnit => ({
  ...unit,
  quantity_multiplier: String(unit.quantity_multiplier),
});

// List sellable units for an item
export const listSellableUnits = async (
  itemId: number,
  params?: {
    site_id?: number;
    is_active_only?: boolean;
  }
): Promise<SellableUnit[]> => {
  try {
    const response = await axiosInstance.get<SellableUnit[]>(
      `/items/${itemId}/sellable-units/`,
      { params }
    );
    return response.data.map(parseSellableUnitResponse);
  } catch (error) {
    console.error(`Failed to list sellable units for item ${itemId}:`, error);
    throw error;
  }
};

// Get single sellable unit
export const getSellableUnit = async (unitId: number): Promise<SellableUnit> => {
  try {
    const response = await axiosInstance.get<SellableUnit>(`/sellable-units/${unitId}`);
    return parseSellableUnitResponse(response.data);
  } catch (error) {
    console.error(`Failed to get sellable unit ${unitId}:`, error);
    throw error;
  }
};

// Create sellable unit
export const createSellableUnit = async (
  itemId: number,
  data: SellableUnitCreate
): Promise<SellableUnit> => {
  try {
    const payload = {
      ...data,
      quantity_multiplier: Number(data.quantity_multiplier),
    };
    const response = await axiosInstance.post<SellableUnit>(
      `/items/${itemId}/sellable-units/`,
      payload
    );
    return parseSellableUnitResponse(response.data);
  } catch (error) {
    console.error(`Failed to create sellable unit for item ${itemId}:`, error);
    throw error;
  }
};

// Update sellable unit
export const updateSellableUnit = async (
  unitId: number,
  data: SellableUnitUpdate
): Promise<SellableUnit> => {
  try {
    const payload = {
      ...data,
      quantity_multiplier: data.quantity_multiplier 
        ? Number(data.quantity_multiplier) 
        : undefined,
    };
    const response = await axiosInstance.patch<SellableUnit>(
      `/sellable-units/${unitId}`,
      payload
    );
    return parseSellableUnitResponse(response.data);
  } catch (error) {
    console.error(`Failed to update sellable unit ${unitId}:`, error);
    throw error;
  }
};

// Delete sellable unit
export const deleteSellableUnit = async (unitId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/sellable-units/${unitId}`);
  } catch (error) {
    console.error(`Failed to delete sellable unit ${unitId}:`, error);
    throw error;
  }
};

// Sync items with sellable units (for mobile)
export const syncItemsWithSellableUnits = async (params?: {
  last_sync?: string;
  site_id?: number;
  is_active_only?: boolean;
}): Promise<SellableUnitsSyncResponse> => {
  try {
    const response = await axiosInstance.get<SellableUnitsSyncResponse>(
      '/items/with-sellable-units/sync',
      { params }
    );
    return {
      ...response.data,
      items: response.data.items.map(item => ({
        ...item,
        sellable_units: item.sellable_units.map(parseSellableUnitResponse),
      })),
    };
  } catch (error) {
    console.error('Failed to sync items with sellable units:', error);
    throw error;
  }
};